<script lang="ts">
    import {focusOnMount} from '../../internal/use.focus-on-mount';
    import {formControls} from '../../internal/forms/use.form-controls';
    import {touched} from '../../internal/use.touched';
    import {style} from 'svelte-forms';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import Description from '../../description/Description.svelte';
    import type {DateValueEditorLocalizations, DateValueEditorOptions, DateValueEditorValidations} from './types';
    import type {FormControlOptions} from '../../internal/forms/types';
    import type {ValueEditorSize} from '../../types';
    import type {Injector} from '../../../../svelte/types';
    import {getContext} from 'svelte';
    import type {FormControl} from '../../internal/forms/form-control';
    import {FormControlBuilder} from '../../internal/forms/form-control';
    import {createInputDescription} from '../../description/utils';
    import {onDestroy} from 'svelte';
    import {EDITORS_LOCALIZE} from '../../context-keys';
    import type {DatePickerPhase} from './utils';
    import {createLocalizationForLocale, onlyDateFormatter, onlyDateParser, validatorFactory} from './utils';
    import {dateFormatter, dateParser} from '../_shared/date-time-utils';
    import {Popover, PopoverButton, PopoverPanel} from '@rgossiaux/svelte-headlessui';
    import {createPopperActions} from 'svelte-popperjs';
    import SveltyPicker from 'svelty-picker';
    import {INJECTOR_KEY} from '../../../../svelte/utils';
    import {CurrentLanguageProvider, SHOW_LOC_MESSAGES_LANGUAGE} from '../../../providers/current-language.provider';
    import {DateTime} from 'luxon';
    import type {EditorsLocalizationFunction} from '../../localizations/types';
    import {required} from '../../internal/forms/use.required';
    import {getLogger} from '../../../../svelte/context';


    export let model: string;
    export let formControlOptions: FormControlOptions;

    export let editorId: string;
    export let editorName: string;
    export let placeholder: string;

    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: DateValueEditorOptions;
    export let validations: DateValueEditorValidations;


    const injector: Injector = getContext(INJECTOR_KEY);
    const currentLanguage: string | undefined = injector.getByToken(CurrentLanguageProvider.providerName);
    const localize: EditorsLocalizationFunction<DateValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);

    const formControl = FormControlBuilder.for(editorId, editorName, model)
        .withValidators(validatorFactory(validations))
        .withOptions(formControlOptions)
        .withParsers([(value: string) => value.trim(), dateParser({customViewFormat: options.viewFormat})])
        .withModifiers([onlyDateParser(options)])
        .withFormatters([dateFormatter({customViewFormat: options.viewFormat}), onlyDateFormatter(options)])
        .build(getLogger());

    const subscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    onDestroy(() => {
        subscription.unsubscribe();
    })

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});

    const datetimePickerLocalization = (!!currentLanguage && currentLanguage !== SHOW_LOC_MESSAGES_LANGUAGE) ? createLocalizationForLocale(currentLanguage) : undefined;

    const [popperRef, popperContent] = createPopperActions();
    const popperOptions = {
        placement: 'bottom-end',
        modifiers: [{name: 'offset', options: {offset: [0, 8]}}],
    };

    let inputElement: HTMLElement;
    let datePickerInstance: {getLastPickerPhase: () => DatePickerPhase | string}

    function onPickerChange(event: CustomEvent<string>, pickerCloseHandler: () => void) {
        formControl.setViewValue(event.detail ?? '');

        // if selecting date only close immediately
        if (options.maximumGranularity === 'date-only') {
            pickerCloseHandler();
            return;
        }

        // if selecting date and time close only after selecting minutes
        if (datePickerInstance.getLastPickerPhase() === 'minute') {
            pickerCloseHandler();
        }
    }


    function popoverCloseHandleAction() {
        return {
            destroy: () => formControl.setTouched()
        }
    }

    export function getFormController(): FormControl<string> {
        return formControl;
    }
</script>

<template>

    <div class="input-group input-group-{size}">

        <input class="form-control input-{size}"
               type="text"
               id={editorId}
               name={editorName}
               {placeholder}
               disabled={isDisabled}
               use:required={validations.required}
               use:focusOnMount={isFocused}
               use:formControls={formControl}
               use:touched={{formControl}}
               use:style={{field: formControl.getFieldStateAsStore()}}
               use:ariaInvalid={formControl}
               use:ariaErrormessage={formControl}
               use:ariaDescribedby={inputDescription}

               bind:this={inputElement}
               data-main-input/>

        <Popover as="span" class="input-group-btn" let:close={closePopoverAndFocusElement}>
            <PopoverButton class="btn btn-default" use={[popperRef]} disabled={isDisabled}>
                <span class="glyphicon glyphicon-calendar" aria-hidden="true"></span>
                <span class="sr-only">{localize('openCalendar')}</span>
            </PopoverButton>

            <PopoverPanel use={[[popperContent, popperOptions], popoverCloseHandleAction]} style="display: block; z-index: 1060">
                <div class="datetime-picker-container">
                    <SveltyPicker bind:this={datePickerInstance}
                                  inputClasses="hidden"
                                  format={options.maximumGranularity === 'date-only' ? 'd.m.yyyy' : 'd.m.yyyy h:ii'}
                                  initialDate={model ? DateTime.fromISO(model).toJSDate() : null}
                                  startDate={validations.minDate ? DateTime.fromISO(validations.minDate).toJSDate() : null}
                                  endDate={validations.maxDate ? DateTime.fromISO(validations.maxDate).toJSDate() : null}
                                  i18n={datetimePickerLocalization}
                                  disabled={isDisabled}
                                  pickerOnly={true}
                                  todayBtn={false}
                                  clearBtn={false}
                                  inputElement={null}
                                  on:change={(event) => {onPickerChange(event, () => closePopoverAndFocusElement(inputElement))}}>
                    </SveltyPicker>
                </div>
            </PopoverPanel>
        </Popover>

    </div>

    <ErrorMessages formController={formControl} {forceShowErrors} {size}/>

    <Description {inputDescription}/>
</template>

<style>
    :global(.datetime-picker-container *) {
        font-size: 1.6rem;
    }

    :global(.datetime-picker-container th) {
        text-align: center;
    }
</style>